import { getDeviceId } from '@libs/utils';

declare const APP_TITLE: string;

const STORAGE_KEY = 'webauthn_encrypted_passcode';

function bufToBase64(buf: ArrayBuffer | Uint8Array): string {
    const uint8Array = buf instanceof Uint8Array ? buf : new Uint8Array(buf);
    return btoa(String.fromCharCode(...uint8Array));
}

function base64ToBuf(base64: string): ArrayBuffer {
    // This function needs to handle both standard Base64 (from cipher/iv)
    // and Base64URL (from credentialId).
    let standardBase64 = base64.replace(/-/g, '+').replace(/_/g, '/');

    // Add padding if it's missing, as atob requires it.
    while (standardBase64.length % 4) {
        standardBase64 += '=';
    }

    const binary = atob(standardBase64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes.buffer;
}

const getCryptoKey = async (rawId: ArrayBuffer): Promise<CryptoKey> => {
    const keyMaterial = await window.crypto.subtle.digest('SHA-256', rawId);
    return window.crypto.subtle.importKey('raw', keyMaterial, 'AES-GCM', true, ['encrypt', 'decrypt']);
};

/**
 * Registers a new credential and encrypts the given passcode with a key derived from it.
 * @param passcode The 4-digit passcode to encrypt.
 */
export const registerAndEncryptPasscode = async (passcode: string): Promise<void> => {
    try {
        // Clear any old data first to ensure a clean slate
        // localStorage.removeItem(STORAGE_KEY);

        // Ensure deviceId is not null and encode it for the user ID.
        const userId = new TextEncoder().encode(getDeviceId() ?? 'anonymous_user');
        const hostname = window.location.hostname;
        const credential = (await navigator.credentials.create({
            publicKey: {
                challenge: window.crypto.getRandomValues(new Uint8Array(32)),
                rp: { name: APP_TITLE, id: hostname },
                user: {
                    id: userId,
                    name: `user@${hostname}`,
                    displayName: `User on ${hostname}`,
                },
                pubKeyCredParams: [{ alg: -7, type: 'public-key' }], // ES256
                authenticatorSelection: {
                    authenticatorAttachment: 'platform',
                    requireResidentKey: false,
                    userVerification: 'required',
                },
                timeout: 60000,
            },
        })) as PublicKeyCredential | null;

        if (!credential) {
            throw new Error('Credential creation failed.');
        }

        const cryptoKey = await getCryptoKey(credential.rawId as ArrayBuffer);
        const encodedPasscode = new TextEncoder().encode(passcode);
        const iv = window.crypto.getRandomValues(new Uint8Array(12)); // Recommended IV size for AES-GCM

        const cipher = await window.crypto.subtle.encrypt({ name: 'AES-GCM', iv }, cryptoKey, encodedPasscode);

        const storedData = {
            cipher: bufToBase64(cipher),
            iv: bufToBase64(iv), // Store the Uint8Array directly
            credentialId: credential.id,
        };

        localStorage.setItem(STORAGE_KEY, JSON.stringify(storedData));
        console.log('Passcode registered and encrypted successfully!');
    } catch (error) {
        console.error('Error during WebAuthn registration:', error);
        throw error; // Re-throw to be handled by the caller
    }
};

/**
 * Authenticates the user and decrypts the stored passcode.
 * @returns The decrypted passcode.
 */
export const authenticateAndDecryptPasscode = async (): Promise<string | null> => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (!stored) {
            console.log('No encrypted passcode found in storage.');
            return null;
        }

        const data = JSON.parse(stored);
        if (!data.cipher || !data.iv || !data.credentialId) {
            console.error('Invalid stored data format. Clearing stored data.');
            localStorage.removeItem(STORAGE_KEY);
            return null;
        }

        const credential = (await navigator.credentials.get({
            publicKey: {
                challenge: window.crypto.getRandomValues(new Uint8Array(32)),
                timeout: 60000,
                userVerification: 'required',
                allowCredentials: [
                    {
                        type: 'public-key',
                        id: base64ToBuf(data.credentialId),
                    },
                ],
            },
        })) as PublicKeyCredential | null;

        if (!credential) {
            throw new Error('Authentication failed.');
        }

        const cryptoKey = await getCryptoKey(credential.rawId as ArrayBuffer);
        const cipher = base64ToBuf(data.cipher);
        const iv = new Uint8Array(base64ToBuf(data.iv)); // Decrypt requires a TypedArray

        const decrypted = await window.crypto.subtle.decrypt({ name: 'AES-GCM', iv }, cryptoKey, cipher);

        return new TextDecoder().decode(decrypted);
    } catch (error) {
        if (error instanceof Error && error.name === 'NotAllowedError') {
            console.log('WebAuthn authentication canceled by user.');
        }
        // if (error instanceof DOMException && error.name === 'OperationError') {
        //     console.error('Decryption failed, likely due to key mismatch. Clearing stored data.');
        //     localStorage.removeItem(STORAGE_KEY);
        // }
        console.error('Error during WebAuthn authentication:', error);
        throw error; // Re-throw to be handled by the caller
    }
};

/**
 * Checks if a passcode is already stored.
 */
export const isPasscodeStored = (): boolean => {
    const stored = localStorage.getItem(STORAGE_KEY);
    if (!stored) return false;
    try {
        const data = JSON.parse(stored);
        return !!data.credentialId;
    } catch (e) {
        console.warn('Cannot read passcode.', e);
        return false;
    }
};
